/**
 * CHOREOGRAPHY TIMING (B4) — every duration in spec §E as a pure function.
 * D is the route length in km, L in metres. No DOM, no map: unit-tested against the §E table.
 *
 *   Tc     = clamp(1100 + 320·log2(1 + D/4), 1100, 2400)     camera
 *   Td     = clamp(1100 + 460·log2(1 + D/4), 1100, 3400)     line draw
 *   Tarr   = 0.4·Tc + Td                                     arrival (card enters at Tarr − 120)
 *   Tp     = clamp(1800 + 300·log2(1 + D/4), 1800, 3200)     idle packet travel
 *   Tdrive = clamp(8000 + 6000·log2(1 + D/4), 10000, 40000)  the drive
 *   z_d    = clamp(15.6 − 0.55·log2(1 + D/4), 13.6, 15.2)    drive zoom
 *
 * Bloom (§E.4): s = clamp(1400/n, 40, 160), dᵢ = 900 + 500·√(Lᵢ/Lmax).
 * Idle (§E.5): σ = clamp(0.03·L, 150, 1200); interval 7000 ± 1500 (blooms 11000 ± 2000).
 * Drive (§E.6): look-ahead clamp(0.012·L, 250, 1500) m; p eased over the first and last 1500 ms.
 */

export const clampN = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);

/** log2(1 + D/4): the common growth term of every §E formula. */
export const growth = (D: number): number => Math.log2(1 + Math.max(0, Number.isFinite(D) ? D : 0) / 4);

export const cameraMs = (D: number): number => clampN(1100 + 320 * growth(D), 1100, 2400);
export const drawMs = (D: number): number => clampN(1100 + 460 * growth(D), 1100, 3400);
/** The line starts drawing at 0.4·Tc. */
export const drawStartMs = (D: number): number => 0.4 * cameraMs(D);
export const arriveMs = (D: number): number => drawStartMs(D) + drawMs(D);
export const packetMs = (D: number): number => clampN(1800 + 300 * growth(D), 1800, 3200);
export const driveMs = (D: number): number => clampN(8000 + 6000 * growth(D), 10000, 40000);
export const driveZoom = (D: number): number => clampN(15.6 - 0.55 * growth(D), 13.6, 15.2);

/** The card (and the meter's dock) lead the arrival by this much (§E.1). */
export const CARD_LEAD_MS = 120;
/** Exits run at 0.7× their entry (brand rule). */
export const EXIT = 0.7;
/** A → B: A retracts at 0.5·Td(A) (§E.3). */
export const AB_RETRACT = 0.5;

/** §E.2 deselect: line retract 0.7·Td, camera home 0.7·Tc. */
export function deselectMs(D: number): { line: number; camera: number } {
  return { line: EXIT * drawMs(D), camera: EXIT * cameraMs(D) };
}

/** Every §E.1 moment for a route of D km (ms from the selection). */
export interface SelectTimeline {
  Tc: number;
  Td: number;
  drawAt: number; //  0.4·Tc
  Tarr: number; //    0.4·Tc + Td
  cardAt: number; //  Tarr − 120
  stillAt: number; // Tarr + 1920
  packetAt: number; // Tarr + 3000 (first idle packet)
  Tp: number;
}
export function selectTimeline(D: number): SelectTimeline {
  const Tc = cameraMs(D);
  const Td = drawMs(D);
  const drawAt = 0.4 * Tc;
  const Tarr = drawAt + Td;
  return { Tc, Td, drawAt, Tarr, cardAt: Tarr - CARD_LEAD_MS, stillAt: Tarr + 1920, packetAt: Tarr + 3000, Tp: packetMs(D) };
}

/* ── the tree rings, the land answering (§E.1) ─────────────────────── */

export const RINGS = { count: 3, staggerMs: 160, durationMs: 1600 } as const;
export const ringRadiusPx = (kiosk: boolean): number => (kiosk ? 72 : 56);
export const litRadiusPx = (kiosk: boolean): number => (kiosk ? 130 : 110);
export const LIT = { riseMs: 1600, rest: 0.6, siteRiseMs: 1100 } as const;

/* ── bloom (§E.4) ──────────────────────────────────────────────────── */

export const bloomStagger = (n: number): number => clampN(1400 / Math.max(1, n), 40, 160);
export const bloomDrawMs = (L: number, Lmax: number): number => 900 + 500 * Math.sqrt(Lmax > 0 ? clampN(L / Lmax, 0, 1) : 1);

export interface BloomItem {
  id: string;
  L: number; //       metres
  minutes: number; // sort key (display minutes)
}
export interface BloomSlot extends BloomItem {
  index: number;
  start: number; //  ms from the bloom's t = 0 (0.4·Tcam + i·s)
  draw: number; //   dᵢ
  arrive: number; // start + dᵢ
}
export interface BloomPlan {
  Tcam: number;
  s: number;
  slots: BloomSlot[]; // nearest first (minutes ascending)
  doneAt: number; //   the last arrival
}
/** The bloom schedule: nearest (by minutes) first, stagger s, per-route dᵢ; Tcam = Tc(Dmax). */
export function bloomPlan(items: BloomItem[]): BloomPlan {
  const sorted = [...items].sort((a, b) => a.minutes - b.minutes || a.L - b.L || a.id.localeCompare(b.id));
  const n = sorted.length;
  const s = bloomStagger(n);
  const Lmax = sorted.reduce((m, x) => Math.max(m, x.L), 0);
  const Tcam = cameraMs(Lmax / 1000);
  const t0 = 0.4 * Tcam;
  const slots = sorted.map((x, index) => {
    const start = t0 + index * s;
    const draw = bloomDrawMs(x.L, Lmax);
    return { ...x, index, start, draw, arrive: start + draw };
  });
  const doneAt = slots.reduce((m, x) => Math.max(m, x.arrive), 0);
  return { Tcam, s, slots, doneAt };
}
/** Bloom exit: farthest (by L) first with the same stagger, each over 0.7·dᵢ. */
export function bloomExitPlan(plan: Pick<BloomPlan, "s" | "slots">): Array<{ id: string; L: number; start: number; retract: number }> {
  return [...plan.slots]
    .sort((a, b) => b.L - a.L || a.id.localeCompare(b.id))
    .map((x, i) => ({ id: x.id, L: x.L, start: i * plan.s, retract: EXIT * x.draw }));
}

/* ── idle life (§E.5) ──────────────────────────────────────────────── */

export const packetSigmaM = (L: number): number => clampN(0.03 * L, 150, 1200);
/** 7000 ± 1500 ms, uniform (u in [0, 1)). */
export const packetIntervalMs = (u: number): number => 7000 + (clampN(u, 0, 1) * 2 - 1) * 1500;
/** Blooms: one packet per route every 11 ± 2 s. */
export const bloomPacketIntervalMs = (u: number): number => 11000 + (clampN(u, 0, 1) * 2 - 1) * 2000;
export const FIRST_PACKET_AFTER_MS = 3000;

/* ── the drive (§E.6) ──────────────────────────────────────────────── */

export const DRIVE_PITCH = 60;
export const DRIVE_ENTER_MS = 1400;
export const DRIVE_RAMP_MS = 1500;
export const DRIVE_ARRIVE_HOLD_MS = 2400;
export const DRIVE_START_BEARING_M = 300;
export const lookAheadM = (L: number): number => clampN(0.012 * L, 250, 1500);

/**
 * Drive progress p at t (0..T): the speed ramps up over the first `ramp` ms and down over the last (a
 * smoothstep speed profile, so p is C¹ and eased at both ends), linear in between. p(0) = 0, p(T) = 1.
 * Identical to the card's driveP (B8 timing.ts), so the HUD's caption-span estimates match the ride.
 */
export function driveP(t: number, T: number, ramp = DRIVE_RAMP_MS): number {
  if (!(T > 0)) return 1;
  const u = clampN(t / T, 0, 1);
  const w = clampN(ramp / T, 0, 0.5);
  if (w === 0) return u;
  const S = (x: number): number => x * x * x - (x * x * x * x) / 2; // ∫ smoothstep, S(1) = 0.5
  const area = 1 - w;
  let F: number;
  if (u < w) F = w * S(u / w);
  else if (u <= 1 - w) F = w / 2 + (u - w);
  else F = area - w * S((1 - u) / w);
  return clampN(F / area, 0, 1);
}

/** Inverse of driveP (bisection): the time at which the ride reaches p (resume after an interruption). */
export function driveTimeAt(p: number, T: number, ramp = DRIVE_RAMP_MS): number {
  if (p <= 0) return 0;
  if (p >= 1) return T;
  let lo = 0;
  let hi = T;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (driveP(mid, T, ramp) < p) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/* ── reduced motion (§E.10) ────────────────────────────────────────── */

export const REDUCED = { fade: 160, line: 240, camera: 240, bloom: 240 } as const;

/* ── the intro (§E.7) ──────────────────────────────────────────────── */

export const INTRO_BEATS = {
  start: 0,
  line1: 400,
  contours: 900,
  line2: 3300,
  sunrise: 3700,
  fly: 5200,
  site: 7200,
  rings: 7900,
  ui: 8300,
  done: 9200,
} as const;
export const INTRO = { contoursMs: 2400, flyMs: 2600, siteMs: 1100, siteFillMs: 640, shortMs: 420, curve: 1.42 } as const;
